import { Injectable, NgZone } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';

@Injectable({
  providedIn: 'root'
})
export class CustomOverlayPositionService {

  private overlayObserver!: MutationObserver;

  constructor(
    private overlayContainer: OverlayContainer,
    private zone: NgZone
  ) {
    // console.log('CustomOverlayPositionService initialized');
    this.init();
  }

  private init(): void {
    this.zone.runOutsideAngular(() => {
      const overlayEl = this.overlayContainer.getContainerElement();

      this.overlayObserver = new MutationObserver(() => {
        const panels =
          overlayEl.querySelectorAll<HTMLElement>('.custom-select-panel');

        panels.forEach(panel => this.deferLock(panel));
      });

      this.overlayObserver.observe(overlayEl, {
        childList: true,
        subtree: true
      });
    });
  }

  /** ------------------------------------------------
   *  Wait for layout to stabilize (CRITICAL for prod)
   * ------------------------------------------------ */
  private deferLock(panel: HTMLElement): void {
    if (panel.dataset['locked'] === 'true') return;

    // Two RAFs guarantee final layout in prod builds
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.lockAndAdjust(panel);
      });
    });
  }

  private lockAndAdjust(panel: HTMLElement): void {
    if (panel.dataset['locked'] === 'true') return;

    const viewportWidth = document.documentElement.clientWidth;
    const rect = panel.getBoundingClientRect();
    const margin = 8;

    /* -----------------------------------------
     * 1. LOCK WIDTH (prevents shrink)
     * ----------------------------------------- */
    const width = rect.width;
    panel.style.width = `${width}px`;
    panel.style.minWidth = `${width}px`;
    panel.style.maxWidth = `${width}px`;

    /* -----------------------------------------
     * 2. CLAMP INSIDE VIEWPORT
     * ----------------------------------------- */
    let shiftX = 0;

    // Right overflow
    if (rect.right > viewportWidth - margin) {
      shiftX = viewportWidth - rect.right - margin;
    }

    // Left overflow
    if (rect.left + shiftX < margin) {
      shiftX = margin - rect.left;
    }

    /* -----------------------------------------
     * 3. APPLY TRANSFORM
     * ----------------------------------------- */
    if (shiftX !== 0) {
      panel.style.transform = `translate3d(${shiftX}px, 0, 0)`;
    } else {
      panel.style.transform = '';
    }

    /* -----------------------------------------
     * 4. STABILITY FLAGS
     * ----------------------------------------- */
    panel.style.left = 'auto';
    panel.style.right = 'auto';
    panel.style.zIndex = '1200';
    panel.style.willChange = 'transform';

    panel.dataset['locked'] = 'true';
  }
}
